import { useEffect, useRef, useState } from 'react';

interface PullToRefreshOptions {
  /** Async refresh handler. Spinner stays visible until this resolves. */
  onRefresh: () => Promise<unknown> | void;
  /** Pixels of pull required to trigger refresh. Default 64. */
  threshold?: number;
  /** Visual maximum the indicator can grow to. Default 96. */
  maxDistance?: number;
}

/**
 * Touch-only pull-to-refresh gesture on a scrollable element.
 *
 * Attach the returned `ref` to a vertically-scrollable container. While the
 * container is at `scrollTop === 0` and the user drags downward, `pullDistance`
 * grows (dampened so it feels rubbery). Releasing past the threshold fires
 * `onRefresh` and keeps `isRefreshing` true until the promise resolves.
 *
 * Mouse/desktop users never see this — `touchstart` is touch-only.
 */
export function usePullToRefresh<T extends HTMLElement>({
  onRefresh,
  threshold = 64,
  maxDistance = 96,
}: PullToRefreshOptions) {
  const ref = useRef<T>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const stateRef = useRef({ startY: 0, distance: 0, tracking: false });
  const refreshingRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onStart = (e: TouchEvent) => {
      if (el.scrollTop > 0 || refreshingRef.current) return;
      stateRef.current.startY = e.touches[0]?.clientY ?? 0;
      stateRef.current.distance = 0;
      stateRef.current.tracking = true;
    };

    const onMove = (e: TouchEvent) => {
      const s = stateRef.current;
      if (!s.tracking) return;
      const delta = (e.touches[0]?.clientY ?? 0) - s.startY;
      if (delta <= 0) {
        if (s.distance !== 0) setPullDistance(0);
        s.distance = 0;
        return;
      }
      // Rubber-band: half-rate, capped at maxDistance
      const dampened = Math.min(maxDistance, delta * 0.5);
      s.distance = dampened;
      setPullDistance(dampened);
      // Prevent browser's native overscroll/bounce once the user is clearly pulling
      if (delta > 10 && e.cancelable) e.preventDefault();
    };

    const finish = async () => {
      const s = stateRef.current;
      const distance = s.distance;
      s.tracking = false;
      s.distance = 0;
      setPullDistance(0);

      if (distance >= threshold && !refreshingRef.current) {
        refreshingRef.current = true;
        setIsRefreshing(true);
        try {
          await onRefresh();
        } finally {
          refreshingRef.current = false;
          setIsRefreshing(false);
        }
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', finish);
    el.addEventListener('touchcancel', finish);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', finish);
      el.removeEventListener('touchcancel', finish);
    };
  }, [onRefresh, threshold, maxDistance]);

  return { ref, pullDistance, isRefreshing, threshold };
}
